import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, ShieldWarning, ArrowCounterClockwise, Plus, Minus, X, ClockCounterClockwise, CheckCircle, Prohibit, Trash, Warning } from "@phosphor-icons/react";

export const UserManagement = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.super_admin;
  const [users, setUsers] = useState([]);
  const [log, setLog] = useState([]);
  const [manage, setManage] = useState(null); // user being managed
  const [amount, setAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const loadUsers = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))), []);
  const loadLog = useCallback(() => api.get("/admin/audit-log").then((r) => setLog(r.data)).catch(() => {}), []);

  useEffect(() => { loadUsers(); loadLog(); }, [loadUsers, loadLog]);

  const openManage = (u) => { setManage(u); setAmount(""); setCreditReason(""); setStatusReason(""); setDelConfirm(""); };
  const refresh = () => { loadUsers(); loadLog(); };

  const adjustCredits = async (sign) => {
    const n = parseInt(amount, 10);
    if (!n || n <= 0) { toast.error("Enter a positive amount."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${manage.id}/credits`, { delta: sign * n, reason: creditReason });
      toast.success(`Credits updated → ${data.simon_credits}`);
      setManage((m) => ({ ...m, simon_credits: data.simon_credits }));
      setAmount(""); setCreditReason("");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const changeRole = async (newRole) => {
    if (!newRole || newRole === manage.role) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${manage.id}/role`, { role: newRole });
      toast.success(`Role updated → ${newRole}`);
      setManage((m) => ({ ...m, role: newRole }));
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const changeStatus = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${manage.id}/${action}`, { reason: statusReason });
      toast.success(action === "suspend" ? "Account suspended" : "Account reactivated");
      setManage((m) => ({ ...m, suspended: action === "suspend" }));
      setStatusReason("");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const deleteUser = async () => {
    if ((delConfirm || "").trim().toLowerCase() !== (manage.email || "").toLowerCase()) {
      toast.error("Type the exact email to confirm deletion."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${manage.id}`, { data: { confirm_email: delConfirm.trim(), reason: statusReason } });
      const n = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${data.email} and ${n} related record(s)`);
      setManage(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const fmt = (t) => (t ? new Date(t).toLocaleString() : "—");

  return (
    <div data-testid="user-mgmt-section" className="mt-16">
      <div className="flex items-center gap-2">
        <UsersThree size={20} className="text-cyan-400" weight="duotone" />
        <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400">// User Management</p>
      </div>
      <h2 className="font-mono-p font-bold tracking-tight text-2xl mt-1">
        Users &amp; Accounts {!isAdmin && <span className="text-xs text-zinc-500 align-middle">(read-only)</span>}
      </h2>

      <div className="mt-4 border border-zinc-800">
        <div className="grid grid-cols-12 gap-3 bg-[#0c0e12] px-5 py-3 font-mono-p text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
          <span className="col-span-4">User</span>
          <span className="col-span-2">Role</span>
          <span className="col-span-2 text-right">Credits</span>
          <span className="col-span-2">Status</span>
          <span className="col-span-2 text-right">Action</span>
        </div>
        {users.length === 0 && <p className="text-sm text-zinc-500 py-8 text-center">No users found.</p>}
        {users.map((u, i) => (
          <div key={u.id} data-testid={`user-row-${i}`} className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center border-b border-zinc-900 hover:bg-[#0c0e12] transition-colors">
            <div className="col-span-4 min-w-0">
              <p className="text-sm text-zinc-200 truncate">{u.email}</p>
              <p className="text-[11px] text-zinc-500 truncate">{u.name || "—"}</p>
            </div>
            <span className="col-span-2 font-mono-p text-[11px] uppercase text-zinc-400">{u.role}</span>
            <span className="col-span-2 text-right font-mono-p text-sm text-cyan-300" data-testid={`user-credits-${i}`}>{u.simon_credits}</span>
            <span className="col-span-2 flex items-center gap-1.5 text-xs">
              {u.suspended ? (
                <><Prohibit size={14} className="text-red-400" /> <span className="text-red-400" data-testid={`user-status-${i}`}>Suspended</span></>
              ) : (
                <><CheckCircle size={14} className="text-green-400" /> <span className="text-green-400" data-testid={`user-status-${i}`}>Active</span></>
              )}
            </span>
            <div className="col-span-2 text-right">
              {isAdmin ? (
                <button data-testid={`manage-user-${i}`} onClick={() => openManage(u)} className="font-mono-p text-[11px] uppercase tracking-widest border border-zinc-700 text-zinc-200 px-3 py-1.5 hover:border-cyan-400/60 hover:text-cyan-400 transition-colors">Manage</button>
              ) : (
                <span className="text-[11px] text-zinc-600">—</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Audit log */}
      <div data-testid="audit-log-section" className="mt-12">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={18} className="text-amber-400" />
          <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-amber-400">// Adjustment &amp; Status Log</p>
        </div>
        <div className="mt-3 border border-zinc-800">
          <div className="grid grid-cols-12 gap-3 bg-[#0c0e12] px-5 py-3 font-mono-p text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
            <span className="col-span-3">When</span>
            <span className="col-span-2">Action</span>
            <span className="col-span-3">Target</span>
            <span className="col-span-1 text-right">Δ</span>
            <span className="col-span-3">Reason / By</span>
          </div>
          {log.length === 0 && <p className="text-sm text-zinc-500 py-6 text-center">No adjustments yet.</p>}
          {log.map((d, i) => (
            <div key={d.id} data-testid={`audit-row-${i}`} className="grid grid-cols-12 gap-3 px-5 py-3 items-start border-b border-zinc-900 text-xs">
              <span className="col-span-3 text-zinc-400">{fmt(d.created_at)}</span>
              <span className="col-span-2 font-mono-p uppercase text-zinc-300">{d.action}</span>
              <span className="col-span-3 text-zinc-400 truncate">{d.target_email}</span>
              <span className={`col-span-1 text-right font-mono-p ${d.amount > 0 ? "text-green-400" : d.amount < 0 ? "text-red-400" : "text-zinc-500"}`}>{d.amount != null ? (d.amount > 0 ? `+${d.amount}` : d.amount) : (d.new_value || "")}</span>
              <span className="col-span-3 text-zinc-500 truncate">{d.reason || "—"} · <span className="text-zinc-600">{d.actor_email}</span></span>
            </div>
          ))}
        </div>
      </div>

      {/* Manage modal (admin only) */}
      {manage && isAdmin && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="manage-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setManage(null)} />
          <div className="relative w-full max-w-md bg-[#0b0e13] border border-cyan-400/40 p-7">
            <button onClick={() => setManage(null)} data-testid="manage-close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20} weight="bold" /></button>
            <p className="font-mono-p text-xs uppercase tracking-widest text-cyan-400">Manage User</p>
            <h3 className="font-mono-p font-bold text-lg text-white mt-1 truncate">{manage.email}</h3>
            <p className="text-xs text-zinc-500">Role: {manage.role} · Credits: <span className="text-cyan-300 font-bold">{manage.simon_credits}</span> · {manage.suspended ? <span className="text-red-400">Suspended</span> : <span className="text-green-400">Active</span>}</p>

            <div data-testid="manage-details" className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border border-zinc-800 bg-black/40 p-3">
              <div>
                <p className="font-mono-p text-[10px] uppercase tracking-widest text-zinc-600">Member Number</p>
                <p data-testid="manage-member" className="text-sm text-cyan-300 font-mono-p">{manage.member_number || <span className="text-zinc-700">—</span>}</p>
              </div>
              <div>
                <p className="font-mono-p text-[10px] uppercase tracking-widest text-zinc-600">Plan</p>
                <p data-testid="manage-plan" className="text-sm text-zinc-200">{manage.plan_name || <span className="text-zinc-700">—</span>}</p>
              </div>
              <div>
                <p className="font-mono-p text-[10px] uppercase tracking-widest text-zinc-600">Registration Date</p>
                <p data-testid="manage-regdate" className="text-sm text-zinc-200">{manage.created_at ? new Date(manage.created_at).toLocaleDateString() : <span className="text-zinc-700">—</span>}</p>
              </div>
              <div>
                <p className="font-mono-p text-[10px] uppercase tracking-widest text-zinc-600">Registered Email</p>
                <p data-testid="manage-regemail" className="text-sm text-zinc-200 truncate">{manage.email}</p>
              </div>
            </div>

            <div className="mt-5 border-t border-zinc-800 pt-4">
              <label className="font-mono-p text-[11px] uppercase tracking-widest text-zinc-500">Adjust Credits</label>
              <div className="mt-2 flex gap-2">
                <input data-testid="credit-amount-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="w-28 bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none" />
                <input data-testid="credit-reason-input" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Reason" className="flex-1 bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none" />
              </div>
              <div className="mt-2 flex gap-2">
                <button data-testid="credit-add-btn" disabled={busy} onClick={() => adjustCredits(1)} className="flex-1 flex items-center justify-center gap-1.5 font-mono-p text-xs uppercase tracking-widest bg-green-500/90 text-black py-2 font-bold hover:bg-green-400 transition-colors disabled:opacity-50"><Plus size={14} weight="bold" /> Add</button>
                <button data-testid="credit-remove-btn" disabled={busy} onClick={() => adjustCredits(-1)} className="flex-1 flex items-center justify-center gap-1.5 font-mono-p text-xs uppercase tracking-widest bg-red-500/90 text-black py-2 font-bold hover:bg-red-400 transition-colors disabled:opacity-50"><Minus size={14} weight="bold" /> Remove</button>
              </div>
            </div>

            <div className="mt-5 border-t border-zinc-800 pt-4">
              <label className="font-mono-p text-[11px] uppercase tracking-widest text-zinc-500">Role &amp; Access</label>
              <select
                data-testid="role-select"
                value={manage.role}
                disabled={busy || manage.id === user?.id}
                onChange={(e) => changeRole(e.target.value)}
                className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none disabled:opacity-50"
              >
                <option value="user">Customer — no admin or Support Desk</option>
                <option value="support">Support — can run the Support Desk</option>
                <option value="admin">Admin — full access</option>
              </select>
              <p className="text-[11px] text-zinc-500 mt-2">
                {manage.id === user?.id
                  ? "You cannot change your own role."
                  : "Only Support and Admin can open the Support Desk and go online as agents. Customers cannot."}
              </p>
            </div>

            <div className="mt-5 border-t border-zinc-800 pt-4">
              <label className="font-mono-p text-[11px] uppercase tracking-widest text-zinc-500">Account Status</label>
              <input data-testid="status-reason-input" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} placeholder="Reason" className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none" />
              {manage.suspended ? (
                <button data-testid="reactivate-btn" disabled={busy} onClick={() => changeStatus("reactivate")} className="mt-2 w-full flex items-center justify-center gap-1.5 font-mono-p text-xs uppercase tracking-widest bg-green-500/90 text-black py-2.5 font-bold hover:bg-green-400 transition-colors disabled:opacity-50"><ArrowCounterClockwise size={14} weight="bold" /> Reactivate Account</button>
              ) : (
                <button data-testid="suspend-btn" disabled={busy} onClick={() => changeStatus("suspend")} className="mt-2 w-full flex items-center justify-center gap-1.5 font-mono-p text-xs uppercase tracking-widest bg-red-500/90 text-black py-2.5 font-bold hover:bg-red-400 transition-colors disabled:opacity-50"><ShieldWarning size={14} weight="bold" /> Suspend Account</button>
              )}
            </div>

            {manage.id !== user?.id && (
              <div data-testid="danger-zone" className="mt-5 border border-red-500/40 bg-red-500/5 p-4">
                <div className="flex items-center gap-2">
                  <Warning size={16} weight="fill" className="text-red-400" />
                  <label className="font-mono-p text-[11px] uppercase tracking-widest text-red-400">Danger Zone — Permanently Delete</label>
                </div>
                <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">
                  This erases the account and <b>all</b> its data (cards, keys, agents, projects, messages, uploads). This cannot be undone. To confirm, type <span className="text-red-300 font-mono-p">{manage.email}</span> below.
                </p>
                <input
                  data-testid="delete-confirm-input"
                  value={delConfirm}
                  onChange={(e) => setDelConfirm(e.target.value)}
                  placeholder="Type the email to confirm"
                  className="mt-2 w-full bg-black border border-red-500/40 px-3 py-2 text-sm text-zinc-100 focus:border-red-400 focus:outline-none"
                />
                <button
                  data-testid="delete-user-btn"
                  disabled={busy || delConfirm.trim().toLowerCase() !== (manage.email || "").toLowerCase()}
                  onClick={deleteUser}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 font-mono-p text-xs uppercase tracking-widest bg-red-600 text-white py-2.5 font-bold hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash size={14} weight="bold" /> Delete Account Permanently
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
